import React, { Component } from 'react';
import { compose, withApollo } from 'react-apollo';
import { object } from 'prop-types';

import isEqual from 'lodash.isequal';
import { withAuth, authProptypes } from '../../auth';

import { ConfigureOneContext } from '../../contexts';
import { manualAPIs, Queries } from '../../api';

import { ToolPermissions, BrandHelper } from '../../utils';

import { symbols } from '../../images';
import {
  salestool,
  compare,
  warrantyclaims,
  dealerSuccess,
  logos,
  brochures,
  manuals as manualsRoute
} from '../../routing/Routes';

const withMenu = ComponentToWrap => {
  class MenuComponent extends Component {
    state = {
      menuList: [],
      c1Link: undefined,
      allBrands: [],
      manualData: [],
      loading: true
    };

    static propTypes = {
      authUser: authProptypes.user,
      client: object
    };

    static contextType = ConfigureOneContext;

    async componentDidMount() {
      // Fetch parts and manual data from API
      try {
        const allBrands = await this.getBrands();
        const manualData = await this.getManuals();
        this.setState({ allBrands, manualData, loading: false });
      } catch (e) {
        // fail silently
      }
    }

    componentDidUpdate(prevProps, prevState) {
      // Wait for API data to come in and for the c1Link to come in
      // Or when `authUser` changed
      const { authUser } = this.props;
      const { c1Link } = this.context;
      const hasChanged =
        !isEqual(prevProps.authUser, authUser) || prevState.c1Link !== c1Link;

      if (this.state.loading === false && hasChanged) {
        const partsList = this.getPartsList();
        const menuList = this.buildMenuItems(c1Link, partsList);

        this.setState({ c1Link, menuList });
      }
    }

    getBrands = async () => {
      const { client } = this.props;
      // fetch all brands
      const res = await client.query({
        query: Queries.getBrands
      });

      return res.data.getBrands;
    };

    getManuals = async () => {
      // Fetch all manuals
      const data = await manualAPIs.brands();
      return data;
    };

    getPartsList = () => {
      const { allBrands } = this.state;
      // filter parts brands
      const parts = allBrands && allBrands.filter(brand => !!brand.partsUrl);

      if (parts && parts.length) {
        // populate parts brands
        const newParts = parts.map(brand => ({
          title: brand.name,
          qa: `parts-${brand.name}-sub-menu-button`,
          url: brand.partsUrl,
          external: true
        }));

        return newParts;
      }
    };

    // Builds the current menuList using the c1Link and user's authorization status.
    buildMenuItems = (c1Link, partsList = []) => {
      const { authUser } = this.props;
      const isAuthorized = authUser && authUser.isAuthorized;

      const marketingItems = [
        {
          title: 'Brochures',
          url: brochures,
          toolPermissionKey: ToolPermissions.BROCHURES
        },
        {
          title: 'Logos',
          url: logos,
          toolPermissionKey: ToolPermissions.LOGOS
        },
        {
          title: 'Dealer Success Program',
          url: dealerSuccess,
          qa: 'marketing-support-dealer-success-sub-menu-button',
          toolPermissionKey: ToolPermissions.DEALER_SUCCESS
        }
      ];

      const authorizedMarketingItems = ToolPermissions.filterAuthorizedTools(
        marketingItems,
        authUser
      );

      const getSubItems = subItems =>
        subItems.length !== 1 ? subItems : undefined;

      const replaceParentItemURL = subItems =>
        subItems.length === 1 ? subItems[0].url : undefined;

      const marketingURL = replaceParentItemURL(authorizedMarketingItems);
      const partsURL = replaceParentItemURL(partsList);

      const menuItems = [
        {
          title: 'Home',
          icon: symbols.home,
          url: '/',
          display: isAuthorized
        },
        {
          title: 'Selling',
          icon: symbols.selling,
          url: salestool,
          display: isAuthorized,
          qa: 'sales-guidance-menu-button',
          toolPermissionKey: ToolPermissions.SELLING,
          hasToolOverride: authUser.shouldShowCompareTools === false
        },
        {
          title: 'Learning',
          icon: symbols.learning,
          url: compare,
          display: isAuthorized,
          qa: 'product-education-menu-button',
          toolPermissionKey: ToolPermissions.LEARNING,
          hasToolOverride: authUser.shouldShowCompareTools === false
        },
        {
          title: 'Marketing',
          icon: symbols.marketing,
          display: isAuthorized,
          qa: 'marketing-support-menu-button',
          url: marketingURL,
          subItems: getSubItems(authorizedMarketingItems)
        },
        {
          title: 'Manuals',
          icon: symbols.manuals,
          url: manualsRoute,
          display: isAuthorized,
          qa: 'manuals-menu-button',
          toolPermissionKey: ToolPermissions.MANUALS
        },
        {
          title: 'Warranty',
          display: isAuthorized,
          icon: symbols.warranty,
          url: warrantyclaims,
          qa: 'warranty-menu-button'
        },
        {
          title: 'Parts',
          icon: symbols.parts,
          url: partsURL,
          external: partsURL && partsURL.includes('http'),
          subItems: getSubItems(partsList),
          display: isAuthorized,
          qa: 'parts-menu-button',
          toolPermissionKey: ToolPermissions.PARTS,
          hasToolOverride: BrandHelper.hasAuthorizedPartBrands(partsList)
        },
        {
          title: 'Register a Product',
          icon: symbols.register,
          url: process.env.REACT_APP_WARRANTY,
          external: true,
          display: isAuthorized,
          qa: 'register-product-menu-button'
        },
        {
          title: 'Additional Resources',
          icon: symbols.tools,
          url: 'https://agidealersupport.zendesk.com/hc/en-us',
          external: true,
          display: isAuthorized
        }
      ];

      return ToolPermissions.filterAuthorizedTools(menuItems, authUser);
    };

    render() {
      const { menuList } = this.state;

      return <ComponentToWrap {...this.props} items={menuList} />;
    }
  }

  return compose(
    withAuth,
    withApollo
  )(MenuComponent);
};

export default withMenu;



// WEBPACK FOOTER //
// ./src/components/Menu/withMenu.js